import React, { useEffect, useMemo, useState } from 'react';
import { FaCrown, FaLockOpen } from 'react-icons/fa';
import { FaBan, FaCheck, FaPencil } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { API_BASE, apiFetch } from '../lib/api';

type Role = 'admin' | 'agent';

type TeamUser = {
    id: number;
    name: string;
    code: string;
    role: Role;
    is_banned: number;
    modified_at: string;
    modified_by_name?: string | null;
};

export default function TeamMembers() {
    const [users, setUsers] = useState<TeamUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [role, setRole] = useState<Role>('agent');

    const [creating, setCreating] = useState(false);
    const [updatingRoleId, setUpdatingRoleId] = useState<number | null>(null);
    const [updatingCodeId, setUpdatingCodeId] = useState<number | null>(null);
    const [updatingBanId, setUpdatingBanId] = useState<number | null>(null);
    const [hoveredCodeId, setHoveredCodeId] = useState<number | null>(null);
    const [draftCodes, setDraftCodes] = useState<Record<number, string>>({});

    const showMessage = (text: string, error = false) => {
        if (error) {
            toast.error(text);
        } else {
            toast.success(text);
        }
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_BASE}/api/users`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to load users', true);
                return;
            }

            const rows: TeamUser[] = (data.users ?? []).map((u: any) => ({
                ...u,
                is_banned: Number(u.is_banned ?? 0),
            }));

            setUsers(rows);
            const initialCodes: Record<number, string> = {};
            rows.forEach((u) => {
                initialCodes[u.id] = String(u.code ?? '');
            });
            setDraftCodes(initialCodes);
        } catch {
            showMessage('Failed to load users', true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const handleCreate = async () => {
        const trimmedName = name.trim();
        const trimmedCode = code.trim();
        if (!trimmedName || !trimmedCode) {
            showMessage('Name and code are required', true);
            return;
        }

        setCreating(true);
        try {
            const res = await apiFetch(`${API_BASE}/api/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: trimmedName,
                    code: trimmedCode,
                    role,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to create user', true);
                return;
            }

            setName('');
            setCode('');
            setRole('agent');
            showMessage('User created');
            await loadUsers();
        } catch {
            showMessage('Failed to create user', true);
        } finally {
            setCreating(false);
        }
    };

    const handleRoleChange = async (id: number, nextRole: Role) => {
        setUpdatingRoleId(id);
        try {
            const res = await apiFetch(`${API_BASE}/api/users/${id}/role`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ role: nextRole }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to update role', true);
                return;
            }

            setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role: nextRole } : u)));
            showMessage('Role updated');
        } catch {
            showMessage('Failed to update role', true);
        } finally {
            setUpdatingRoleId(null);
        }
    };

    const handleSaveCode = async (userId: number) => {
        const codeValue = String(draftCodes[userId] ?? '').trim();
        if (!codeValue) {
            showMessage('Login code is required', true);
            return;
        }

        setUpdatingCodeId(userId);
        try {
            const res = await apiFetch(`${API_BASE}/api/users/${userId}/code`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code: codeValue }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to update login code', true);
                return;
            }

            setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, code: codeValue } : u)));
            showMessage('Login code updated');
        } catch {
            showMessage('Failed to update login code', true);
        } finally {
            setUpdatingCodeId(null);
        }
    };

    const handleToggleBan = async (userId: number, nextBanned: boolean) => {
        setUpdatingBanId(userId);
        try {
            const res = await apiFetch(`${API_BASE}/api/users/${userId}/ban`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ banned: nextBanned }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to update ban status', true);
                return;
            }

            setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_banned: nextBanned ? 1 : 0 } : u)));
            showMessage(nextBanned ? 'User banned' : 'User unbanned');
        } catch {
            showMessage('Failed to update ban status', true);
        } finally {
            setUpdatingBanId(null);
        }
    };

    const filteredUsers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) => {
            return u.name.toLowerCase().includes(q)
                || u.role.toLowerCase().includes(q)
                || String(u.code).toLowerCase().includes(q);
        });
    }, [users, searchQuery]);

    return (
        <div className="min-h-screen p-4 md:p-6 bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_#f8fafc_40%,_#eef2ff_100%)]">
            <div className="mb-6 rounded-2xl border border-white/40 bg-white/60 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-5 md:p-6">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-800 mb-1">Team Members</h1>
                <p className="text-sm text-slate-600">Manage users, roles, login codes, and account access.</p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                <div className="xl:col-span-8 rounded-2xl border border-white/40 bg-white/65 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.08)] overflow-auto">
                    <div className="p-4 border-b border-slate-200/70 bg-white/70">
                        <input
                            className="w-full border border-slate-200/80 bg-white/90 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                            placeholder="Search members by name, role, or code"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {loading ? (
                        <div className="p-4 text-slate-600">Loading users...</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="bg-white/70 text-left text-slate-700">
                                    <th className="p-3">Name</th>
                                    <th className="p-3">Login Code</th>
                                    <th className="p-3">Role</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u) => {
                                    const banDisabled = updatingBanId === u.id;
                                    const draftCode = draftCodes[u.id] ?? '';
                                    const isCodeDirty = draftCode !== (u.code ?? '');
                                    return (
                                        <tr key={u.id} className="border-t border-slate-200/70 hover:bg-white/40 transition align-top">
                                            <td className="p-3 font-medium text-slate-800">
                                                <span className="inline-flex items-center gap-2 capitalize">
                                                    {u.name}
                                                    {u.role === 'admin' && (
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[11px] font-semibold"
                                                            title="Admin user"
                                                        >
                                                            <FaCrown className="text-[10px]" />
                                                        </span>
                                                    )}
                                                    {u.is_banned === 1 && (
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[11px] font-semibold"
                                                            title="Banned user"
                                                        >
                                                            Banned
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="p-3 text-slate-700">
                                                <div className="flex flex-row gap-4 items-center">
                                                    <div
                                                        className="relative w-full"
                                                        onMouseEnter={() => setHoveredCodeId(u.id)}
                                                        onMouseLeave={() => setHoveredCodeId(null)}
                                                    >
                                                        <input
                                                            className="w-full border border-slate-200/80 bg-white/90 rounded-lg px-2.5 py-1.5 pr-9"
                                                            type={hoveredCodeId === u.id ? 'text' : 'password'}
                                                            value={draftCode}
                                                            onChange={(e) => setDraftCodes((prev) => ({ ...prev, [u.id]: e.target.value }))}
                                                            disabled={updatingCodeId === u.id}
                                                        />
                                                        <button
                                                            type="button"
                                                            className={`absolute right-2 top-1/2 -translate-y-1/2 text-lg outline-none disabled:cursor-default ${isCodeDirty ? 'text-emerald-500 hover:text-emerald-700 cursor-pointer' : 'text-slate-300'}`}
                                                            data-tooltip-id="my-tooltip"
                                                            data-tooltip-content={isCodeDirty ? 'Save login code' : 'No changes'}
                                                            disabled={!isCodeDirty || updatingCodeId === u.id}
                                                            onClick={() => handleSaveCode(u.id)}
                                                            aria-label={isCodeDirty ? 'Save login code' : 'No login code changes'}
                                                        >
                                                            {isCodeDirty ? <FaCheck /> : <FaPencil />}
                                                        </button>
                                                    </div>
                                                    {u.is_banned === 1 ? (
                                                        <FaLockOpen
                                                            className={`text-green-400 hover:text-green-600 cursor-pointer text-2xl ${banDisabled ? 'opacity-50 pointer-events-none' : ''}`}
                                                            data-tooltip-id="my-tooltip"
                                                            data-tooltip-content="Unban user"
                                                            onClick={() => {
                                                                if (!banDisabled) handleToggleBan(u.id, false);
                                                            }}
                                                        />
                                                    ) : (
                                                        <FaBan
                                                            className={`text-red-400 hover:text-red-600 cursor-pointer text-2xl ${banDisabled ? 'opacity-50 pointer-events-none' : ''}`}
                                                            data-tooltip-id="my-tooltip"
                                                            data-tooltip-content="Ban user"
                                                            onClick={() => {
                                                                if (!banDisabled) handleToggleBan(u.id, true);
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <select
                                                    className="border border-slate-200/80 bg-white/90 rounded-lg px-2.5 py-1.5"
                                                    value={u.role}
                                                    onChange={(e) => handleRoleChange(u.id, e.target.value as Role)}
                                                    disabled={updatingRoleId === u.id}
                                                >
                                                    <option value="agent" className="capitalize">Agent</option>
                                                    <option value="admin" className="capitalize">Admin</option>
                                                </select>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredUsers.length === 0 && (
                                    <tr>
                                        <td className="p-3 text-slate-500" colSpan={3}>
                                            {users.length === 0 ? 'No users found' : 'No matching users'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="xl:col-span-4 rounded-2xl border border-white/40 bg-white/65 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-5">
                    <h2 className="text-lg font-semibold text-slate-800 mb-4">Create User</h2>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Name</label>
                            <input
                                className="w-full border border-slate-200/80 bg-white/80 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-sky-200"
                                placeholder="Enter name of user"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Login Code</label>
                            <input
                                className="w-full border border-slate-200/80 bg-white/80 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-sky-200"
                                placeholder="Enter login code for user"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Role</label>
                            <select
                                className="w-full border border-slate-200/80 bg-white/80 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-sky-200"
                                value={role}
                                onChange={(e) => setRole(e.target.value as Role)}
                            >
                                <option value="agent" className="capitalize">Agent</option>
                                <option value="admin" className="capitalize">Admin</option>
                            </select>
                        </div>
                        <button
                            className="w-full bg-slate-900 text-white rounded-lg px-4 py-2.5 disabled:bg-slate-400 hover:bg-slate-800 transition cursor-pointer"
                            onClick={handleCreate}
                            disabled={creating}
                        >
                            {creating ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
